/**
 * Fetches supervision meeting notes (Clinical Supervision / Clinical Supervisor QRs)
 * from Medplum for the current viewer's scope.
 *
 * - Supervisees see their own Clinical Supervision notes.
 * - Supervisors see their own Clinical Supervisor notes (myNotes), supervisees'
 *   Clinical Supervision notes awaiting cosign (signingQueue), and cosigned
 *   supervisee notes (signedSupervisionNotes).
 */
import { useCallback, useState } from 'react';
import { useMedplum } from '@medplum/react-hooks';
import type { Practitioner, QuestionnaireResponse } from '@medplum/fhirtypes';
import { formatPractitionerStaffLabel } from '@unify/fhir-model/resources/practitioner';
import { useSupervisionScope } from './useSupervisionScope';
import {
  SUPERVISION_MEETING_TAG_SYSTEM,
  SUPERVISION_NOTE_TYPE_TAG_SYSTEM,
  SUPERVISION_NOTE_TYPE_CODES,
} from '../supervisionFhirConstants';
import {
  getSignatureFromQR,
  PROVIDER_SIGNATURE_EXT_URL,
  SUPERVISOR_SIGNATURE_EXT_URL,
} from '../../appointments/fhirNoteMapping';
import { qrToDocumentReviewStatus } from '../utils/questionnaireResponseMappings';
import type { DocumentReviewStatus } from '../types';

export interface SupervisionMeetingNoteItem {
  /** QR resource id (also used as documentIdForReview in the review page). */
  id: string;
  /** 'clinical-supervision' (supervisee note) or 'clinical-supervisor' (supervisor note). */
  noteType: 'clinical-supervision' | 'clinical-supervisor' | 'unknown';
  title: string;
  authorId: string | undefined;
  authorName: string;
  authored: string | undefined;
  status: DocumentReviewStatus;
  commentCount: number;
}

export interface UseSupervisionMeetingNotesResult {
  /** Own notes for the viewer:
   *   - supervisee: their Clinical Supervision notes
   *   - supervisor: their Clinical Supervisor notes */
  myNotes: SupervisionMeetingNoteItem[];
  /** Supervisee Clinical Supervision notes awaiting supervisor cosign (supervisor role only). */
  signingQueue: SupervisionMeetingNoteItem[];
  /** Cosigned supervisee Clinical Supervision notes (supervisor role only). */
  signedSupervisionNotes: SupervisionMeetingNoteItem[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const PAGE_SIZE = 100;

function getNoteType(qr: QuestionnaireResponse): SupervisionMeetingNoteItem['noteType'] {
  const tag = qr.meta?.tag?.find((t) => t.system === SUPERVISION_NOTE_TYPE_TAG_SYSTEM);
  if (tag?.code === SUPERVISION_NOTE_TYPE_CODES['clinical-supervision']) return 'clinical-supervision';
  if (tag?.code === SUPERVISION_NOTE_TYPE_CODES['clinical-supervisor']) return 'clinical-supervisor';
  return 'unknown';
}

function formatPractitionerName(p: Practitioner | undefined): string {
  if (!p) return '—';
  return formatPractitionerStaffLabel(p) || '—';
}

function extractId(ref: string | undefined): string | undefined {
  if (!ref) return undefined;
  const m = ref.match(/Practitioner\/([^/]+)$/);
  return m?.[1];
}

export function useSupervisionMeetingNotes(): UseSupervisionMeetingNotesResult {
  const medplum = useMedplum();
  const { role, viewerPractitionerId, superviseePractitionerIds, scopeReady, isClinicianInScope } =
    useSupervisionScope();

  const [myNotes, setMyNotes] = useState<SupervisionMeetingNoteItem[]>([]);
  const [signingQueue, setSigningQueue] = useState<SupervisionMeetingNoteItem[]>([]);
  const [signedSupervisionNotes, setSignedSupervisionNotes] = useState<SupervisionMeetingNoteItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const doFetch = useCallback(async (): Promise<void> => {
    if (!scopeReady || !viewerPractitionerId) {
      setLoading(true);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      medplum.invalidateSearches('QuestionnaireResponse');
      // Fetch all supervision meeting QRs visible to this user (access policy scopes results).
      const allQRs = await medplum.searchResources('QuestionnaireResponse', {
        _tag: `${SUPERVISION_MEETING_TAG_SYSTEM}|true`,
        _sort: '-_lastUpdated',
        _count: String(PAGE_SIZE),
      });

      // Resolve Practitioner names for authors
      const authorIds = new Set<string>();
      for (const qr of allQRs) {
        const id = extractId(qr.author?.reference);
        if (id) authorIds.add(id);
      }
      const practitionerMap = new Map<string, Practitioner | undefined>();
      await Promise.all(
        [...authorIds].map(async (id) => {
          try {
            const p = await medplum.readResource('Practitioner', id);
            practitionerMap.set(id, p);
          } catch {
            practitionerMap.set(id, undefined);
          }
        }),
      );

      function toItem(qr: QuestionnaireResponse): SupervisionMeetingNoteItem {
        const authorId = extractId(qr.author?.reference);
        const practitioner = authorId ? practitionerMap.get(authorId) : undefined;
        const noteType = getNoteType(qr);
        const templateTitle =
          noteType === 'clinical-supervision'
            ? 'Clinical Supervision Note'
            : noteType === 'clinical-supervisor'
              ? 'Clinical Supervisor Note'
              : 'Supervision Note';
        return {
          id: qr.id!,
          noteType,
          title: templateTitle,
          authorId,
          authorName: formatPractitionerName(practitioner),
          authored: qr.authored ?? qr.meta?.lastUpdated,
          status: qrToDocumentReviewStatus(qr),
          commentCount: 0,
        };
      }

      if (role === 'supervisor') {
        // My supervisor notes: authored by this supervisor, type=clinical-supervisor
        const ownNotes = allQRs
          .filter(
            (qr) =>
              extractId(qr.author?.reference) === viewerPractitionerId &&
              getNoteType(qr) === 'clinical-supervisor',
          )
          .map(toItem);
        setMyNotes(ownNotes);

        // Signing queue: supervisee clinical-supervision notes that are provider-signed but not cosigned
        const queue = allQRs
          .filter((qr) => {
            if (getNoteType(qr) !== 'clinical-supervision') return false;
            const authorId = extractId(qr.author?.reference);
            if (!authorId) return false;
            // Must be one of this supervisor's supervisees
            if (!isClinicianInScope(authorId)) return false;
            const hasProviderSig = !!getSignatureFromQR(qr, PROVIDER_SIGNATURE_EXT_URL);
            const hasSupervisorSig = !!getSignatureFromQR(qr, SUPERVISOR_SIGNATURE_EXT_URL);
            return hasProviderSig && !hasSupervisorSig;
          })
          .map(toItem);
        setSigningQueue(queue);

        // Signed supervision notes: cosigned supervisee clinical-supervision notes
        const signed = allQRs
          .filter((qr) => {
            if (getNoteType(qr) !== 'clinical-supervision') return false;
            const authorId = extractId(qr.author?.reference);
            if (!authorId || !isClinicianInScope(authorId)) return false;
            const hasProviderSig = !!getSignatureFromQR(qr, PROVIDER_SIGNATURE_EXT_URL);
            const hasSupervisorSig = !!getSignatureFromQR(qr, SUPERVISOR_SIGNATURE_EXT_URL);
            return hasProviderSig && hasSupervisorSig;
          })
          .map(toItem);
        setSignedSupervisionNotes(signed);
      } else {
        // Supervisee: own clinical-supervision notes
        const ownNotes = allQRs
          .filter(
            (qr) =>
              extractId(qr.author?.reference) === viewerPractitionerId &&
              getNoteType(qr) === 'clinical-supervision',
          )
          .map(toItem);
        setMyNotes(ownNotes);
        setSigningQueue([]);
        setSignedSupervisionNotes([]);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load supervision meeting notes');
      setMyNotes([]);
      setSigningQueue([]);
      setSignedSupervisionNotes([]);
    } finally {
      setLoading(false);
    }
  }, [medplum, scopeReady, viewerPractitionerId, role, isClinicianInScope, superviseePractitionerIds]);

  return { myNotes, signingQueue, signedSupervisionNotes, loading, error, refetch: doFetch };
}
